/**
 * TextManager - Unified text management API.
 *
 * Combines functionality from:
 * - TextDragger: content for new placements, createAtPosition
 * - TextEditingManager: editing selected text entities
 */

import type { TextDragger } from "../draggers/TextDragger";
import type { TextEditingManager } from "./TextEditingManager";
import type { NoteConfig, Textstyle } from "@/types/legend.types";

/**
 * Dependencies required to create a TextManager.
 */
export interface TextManagerDeps {
  textDragger: TextDragger;
  textEditingManager: TextEditingManager;
}

/**
 * TextManager provides a unified API for text operations.
 *
 * New text placement:
 * - content: Get/set the text content for new placements
 * - createAtPosition: Create text at a specific world position
 *
 * Selected text editing:
 * - canEdit: Check if exactly one text entity is selected
 * - getSelectedText/setSelectedText: Get/set text content
 * - getSelectedTextHeight/setSelectedTextHeight: Get/set text height
 * - setSelectedTextStyle: Apply a text style to selected text
 */
export class TextManager {
  private textDragger: TextDragger;
  private textEditingManager: TextEditingManager;

  constructor(deps: TextManagerDeps) {
    this.textDragger = deps.textDragger;
    this.textEditingManager = deps.textEditingManager;
  }

  // ==================== New Text Placement ====================

  /**
   * Get the current text content for new placements.
   */
  get content(): string {
    return this.textDragger.activeText;
  }

  /**
   * Set the text content for new placements.
   */
  setContent(text: string): void {
    this.textDragger.activeText = text;
  }

  /**
   * Create a text entity at a specific world position.
   * Uses the active NoteConfig for styling.
   * Used for drag-and-drop from toolbar (NoteConfigButton).
   * @param worldX X coordinate in world units
   * @param worldY Y coordinate in world units
   * @param text Optional text content (defaults to content property)
   * @returns true if the text was created successfully
   */
  createAtPosition(
    worldX: number,
    worldY: number,
    text: string,
    config: NoteConfig,
  ) {
    this.textDragger.createAtPosition(worldX, worldY, text, config, true);
  }

  // ==================== Selected Text Editing ====================

  /**
   * Check if the selected entity is specifically an MText (OdDbMText).
   * @returns True if exactly one MText entity is selected
   */
  isSelectedMText(): boolean {
    return this.textEditingManager.isSelectedMText();
  }

  /**
   * Check if exactly one text entity is selected for editing.
   * @returns True if exactly one text (OdDbText or OdDbMText) is selected
   */
  canEdit(): boolean {
    return this.textEditingManager.canEdit();
  }

  /**
   * Get the text content of the selected text entity.
   * @returns The text content, or null if no single text entity is selected
   */
  getSelectedText(): string | null {
    return this.textEditingManager.getSelectedText();
  }

  /**
   * Set the text content of the selected text entity.
   * @param newText The new text content
   * @returns True if the text was successfully set
   */
  setSelectedText(newText: string): boolean {
    return this.textEditingManager.setSelectedText(newText);
  }

  /**
   * Get the text height of the selected text entity.
   * @returns The text height in world units, or null if no single text entity is selected
   */
  getSelectedTextHeight(): number | null {
    return this.textEditingManager.getSelectedTextHeight();
  }

  /**
   * Set the text height of the selected text entity.
   * @param height The new text height in world units
   * @returns True if the text height was successfully set
   */
  setSelectedTextHeight(height: number): boolean {
    return this.textEditingManager.setSelectedTextHeight(height);
  }

  /**
   * Set the text style of the selected text entity.
   * @param style The text style to apply
   * @returns True if the text style was successfully set
   */
  setSelectedTextStyle(style: Textstyle): boolean {
    return this.textEditingManager.setSelectedTextStyle(style);
  }
}
